import { PromoDialogType } from 'persistentContent/promoDialog/usePromoDialogStructs'
import { createCachedReduxSelector } from 'util/createCachedReduxSelector'

export const REGISTER_PROMO_SUPPRESSOR = 'REGISTER_PROMO_SUPPRESSOR'
export const DEREGISTER_PROMO_SUPPRESSOR = 'DEREGISTER_PROMO_SUPPRESSOR'
export const CLEAR_ALL_PROMO_SUPPRESSORS = 'CLEAR_ALL_PROMO_SUPPRESSORS'

export type PromoSuppressionState = {
  suppressors: Record<string, PromoDialogType[]>
}

const initialState: PromoSuppressionState = {
  suppressors: {},
}

export interface RegisterPromoSuppressorAction {
  type: typeof REGISTER_PROMO_SUPPRESSOR
  payload: {
    key: string
    promoTypes: PromoDialogType[]
  }
}

export interface DeregisterPromoSuppressorAction {
  type: typeof DEREGISTER_PROMO_SUPPRESSOR
  payload: {
    key: string
  }
}

export interface ClearAllPromoSuppressorsAction {
  type: typeof CLEAR_ALL_PROMO_SUPPRESSORS
}

export type PromoSuppressionAction =
  | RegisterPromoSuppressorAction
  | DeregisterPromoSuppressorAction
  | ClearAllPromoSuppressorsAction

export const registerPromoSuppressor = (key: string, promoTypes: PromoDialogType[]): RegisterPromoSuppressorAction => ({
  type: REGISTER_PROMO_SUPPRESSOR,
  payload: { key, promoTypes },
})

export const deregisterPromoSuppressor = (key: string): DeregisterPromoSuppressorAction => ({
  type: DEREGISTER_PROMO_SUPPRESSOR,
  payload: { key },
})

export const clearAllPromoSuppressors = (): ClearAllPromoSuppressorsAction => ({
  type: CLEAR_ALL_PROMO_SUPPRESSORS,
})

export default function promoSuppressionReducer(
  state = initialState,
  action: PromoSuppressionAction
): PromoSuppressionState {
  switch (action.type) {
    case REGISTER_PROMO_SUPPRESSOR:
      return {
        ...state,
        suppressors: {
          ...state.suppressors,
          [action.payload.key]: action.payload.promoTypes,
        },
      }
    case DEREGISTER_PROMO_SUPPRESSOR:
      const { [action.payload.key]: removed, ...remainingSuppressors } = state.suppressors
      return {
        ...state,
        suppressors: remainingSuppressors,
      }
    case CLEAR_ALL_PROMO_SUPPRESSORS:
      return {
        ...state,
        suppressors: {},
      }
    default:
      return state
  }
}

export const promoSuppressionSelectors = {
  getSuppressors: createCachedReduxSelector(
    (state: any): Record<string, PromoDialogType[]> => state.promoSuppression?.suppressors || {}
  ),

  isPromoTypeSuppressed: createCachedReduxSelector((state: any, promoType: PromoDialogType): boolean => {
    const suppressors = promoSuppressionSelectors.getSuppressors(state)
    return Object.values(suppressors).some((suppressedTypes) => suppressedTypes.includes(promoType))
  }),

  getSuppressedPromoTypes: createCachedReduxSelector((state: any): PromoDialogType[] => {
    const suppressors = promoSuppressionSelectors.getSuppressors(state)
    const allSuppressedTypes = Object.values(suppressors).flat()
    return [...new Set(allSuppressedTypes)]
  }),

  getSuppressorKeys: createCachedReduxSelector((state: any): string[] => {
    const suppressors = promoSuppressionSelectors.getSuppressors(state)
    return Object.keys(suppressors)
  }),
}
